import { ArrowLeft } from "lucide-react";
import { useMemo } from "react";
import { useData } from "../contexts/DataContext";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";

interface UserDetailsPageProps {
	userId: string;
	onNavigateBack: () => void;
}

export function UserDetailsPage({
	userId,
	onNavigateBack,
}: UserDetailsPageProps) {
	const { users, companies } = useData();

	const memberships = useMemo(
		() => users.filter((user) => user.id === userId),
		[users, userId],
	);

	const user = memberships[0];

	const phoneNumbers = useMemo(
		() =>
			Array.from(
				new Set(
					memberships
						.map((membership) => membership.phoneNumber?.trim())
						.filter(Boolean),
				),
			),
		[memberships],
	);

	const lastLogin = useMemo(() => {
		return memberships.reduce<string | Date | null>((latest, membership) => {
			const candidate =
				(
					membership as typeof membership & {
						lastLoginAt?: Date | string | null;
					}
				).lastLoginAt ??
				membership.lastLogin ??
				null;

			if (!candidate) return latest;
			if (!latest) return candidate;
			return new Date(candidate) > new Date(latest) ? candidate : latest;
		}, null);
	}, [memberships]);

	if (!user || memberships.length === 0) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button variant="ghost" onClick={onNavigateBack}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Users
					</Button>
				</div>
				<Card>
					<CardContent className="py-8">
						<p className="text-center text-slate-500">User not found</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	const formatTimestamp = (timestamp: string | Date | null | undefined) => {
		if (!timestamp) return "N/A";
		return new Date(timestamp).toLocaleString();
	};

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<Button variant="ghost" onClick={onNavigateBack}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Users
					</Button>
				</div>
				<Badge variant="outline">{memberships.length} companies</Badge>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>User Information</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label>User ID</Label>
							<Input value={user.id} disabled className="mt-1" />
						</div>
						<div>
							<Label>Email</Label>
							<Input value={user.email} disabled className="mt-1" />
						</div>
						<div>
							<Label>Display Name</Label>
							<Input
								value={user.displayName || "N/A"}
								disabled
								className="mt-1"
							/>
						</div>
						<div>
							<Label>Phone Numbers</Label>
							<div className="mt-1 flex min-h-10 flex-wrap items-center gap-2 rounded-md border px-3 py-2">
								{phoneNumbers.length > 0 ? (
									phoneNumbers.map((phoneNumber) => (
										<Badge key={phoneNumber} variant="outline">
											{phoneNumber}
										</Badge>
									))
								) : (
									<span className="text-sm text-slate-500">N/A</span>
								)}
							</div>
						</div>
						<div>
							<Label>Last Login</Label>
							<Input
								value={formatTimestamp(lastLogin)}
								disabled
								className="mt-1"
							/>
						</div>
						<div>
							<Label>System Admin</Label>
							<Input
								value={
									memberships.some(
										(membership) => membership.role === "system-admin",
									)
										? "Yes"
										: "No"
								}
								disabled
								className="mt-1"
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Company Memberships</CardTitle>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Company</TableHead>
								<TableHead>Display Name</TableHead>
								<TableHead>Phone Number</TableHead>
								<TableHead>Role</TableHead>
								<TableHead>Last Login</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{memberships.map((membership) => {
								const company = companies.find(
									(candidate) => candidate.id === membership.companyId,
								);

								return (
									<TableRow key={`${membership.id}-${membership.companyId}`}>
										<TableCell>
											{company?.name || membership.companyId}
										</TableCell>
										<TableCell>{membership.displayName || "N/A"}</TableCell>
										<TableCell>{membership.phoneNumber || "N/A"}</TableCell>
										<TableCell>
											<Badge
												variant={
													membership.role === "admin" ||
													membership.role === "system-admin"
														? "default"
														: "outline"
												}
											>
												{membership.role}
											</Badge>
										</TableCell>
										<TableCell>
											{formatTimestamp(
												(
													membership as typeof membership & {
														lastLoginAt?: Date | string | null;
													}
												).lastLoginAt ?? membership.lastLogin,
											)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</div>
	);
}
